import { useCallback, useEffect, useRef, useState } from "react";
import { Dimensions, Linking, View } from "react-native";
import { type AnimatedRef, makeMutable, measure, runOnJS, runOnUI, useAnimatedRef, useFrameCallback } from "react-native-reanimated";
import { authStorage } from "./storage";
type Rect = readonly [top: number, height: number];
type Box = {
  x: number;
  y: number;
  width: number;
  height: number;
};
type WsFrame = {
  t: number;
  direction: "sent" | "received";
  payload: string;
};
type Row = {
  id: number;
  node: View | null;
  text: string;
  animatedRef: AnimatedRef<View>;
};
/** `rects` slot order: list, then members, replies, cards, others, composer. */
type UiFrame = {
  t: number;
  wall: number;
  intervalMs: number | null;
  costMs: number;
  rects: (Rect | null)[];
};
type Mark = "drag-begin" | "row-swap" | "card-mount" | "send";
type ProbeEvent = {
  t: number;
  kind: "scroll";
  y: number;
} | {
  t: number;
  kind: Mark | "list-mount" | "list-unmount";
} | {
  t: number;
  kind: "row-mount" | "row-unmount";
  row: number;
  text: string;
};
type JankParams = {
  hasLoadingEvent: boolean;
};
type ProbeRecord = {
  windowWidth: number;
  streamingListMounted: boolean;
  authReady: boolean;
  loggedIn: boolean;
  loginError: string | null;
  events: ProbeEvent[];
  beacons: {
    n: number;
    t: number;
  }[];
  frames: WsFrame[];
  samples: {
    t: number;
    list: Rect | null;
    members: (Rect | null)[];
    replies: (Rect | null)[];
    cards: (Rect | null)[];
    others: (Rect | null)[];
    composer: Rect | null;
  }[];
  samplerCostsMs: number[];
  uiFrames: UiFrame[];
};
const GLOBAL_KEY = "__chatHarness";
const PREVIOUS_MEMBER_AUTH_KEYS = ["intendedPath", "lastActiveTime", "pendingInviteToken", "codeVerifier"];
const BEACON_SIZE = 16;
const UI_FRAME_BATCH_MS = 250;
const isMeasurable = (node: unknown): node is View => typeof node === "object" && node !== null && "measureInWindow" in node;
function createChatHarness() {
  const rows = new Set<Row>();
  const composers = new Set<Row>();
  const lists = new Set<Row>();
  let nextRowId = 0;
  let setUiSampling: ((active: boolean) => void) | null = null;
  let uiSlots: (AnimatedRef<View> | null)[] = [];
  const uiSlotRefs = makeMutable<(AnimatedRef<View> | null)[]>([]);
  const uiBuffer = makeMutable<UiFrame[]>([]);
  const uiLastFrameAt = makeMutable<number | null>(null);
  let setBeacon: ((n: number) => void) | null = null;
  let targets: {
    members: string[];
    replies: string[];
    cards: (string | null)[];
    others: string[];
  } | null = null;
  const record: ProbeRecord = {
    windowWidth: Dimensions.get("window").width,
    streamingListMounted: false,
    authReady: false,
    loggedIn: false,
    loginError: null,
    events: [],
    beacons: [],
    frames: [],
    samples: [],
    samplerCostsMs: [],
    uiFrames: []
  };
  const receiveUiFrames = (frames: UiFrame[]) => {
    record.uiFrames.push(...frames);
  };
  const box = (node: View | null | undefined): Box | null => {
    let measured: Box | null = null;
    // constraint: Fabric answers measureInWindow synchronously; a later answer would read as missing.
    node?.measureInWindow((x, y, width, height) => {
      measured = {
        x,
        y,
        width,
        height
      };
    });
    return measured;
  };
  const rect = (node: View | null | undefined): Rect | null => {
    const b = box(node);
    return b ? [b.y, b.height] : null;
  };
  // constraint: a case opened again leaves the earlier screen's rows mounted underneath, so the newest match is the one on screen.
  const newestMountedRowFor = (text: string): Row | undefined => [...rows].findLast(row => row.node && row.text.includes(text));
  const newest = <T,>(set: Set<T>): T | undefined => [...set].at(-1);
  const tick = () => {
    if (targets) {
      const started = performance.now();
      const members = targets.members.map(newestMountedRowFor);
      const replies = targets.replies.map(newestMountedRowFor);
      const cards = targets.cards.map(text => text ? newestMountedRowFor(text) : undefined);
      const others = targets.others.map(newestMountedRowFor);
      const composer = newest(composers);
      record.samples.push({
        t: Date.now(),
        list: rect(newest(lists)?.node),
        members: members.map(row => rect(row?.node)),
        replies: replies.map(row => rect(row?.node)),
        cards: cards.map(row => rect(row?.node)),
        others: others.map(row => rect(row?.node)),
        composer: rect(composer?.node)
      });
      record.samplerCostsMs.push(performance.now() - started);
      const slots = [newest(lists), ...members, ...replies, ...cards, ...others, composer].map(row => row?.animatedRef ?? null);
      if (slots.some((ref, i) => ref !== uiSlots[i])) {
        uiSlots = slots;
        uiSlotRefs.value = slots;
      }
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
  const NativeWebSocket = globalThis.WebSocket;
  class RecordingWebSocket extends NativeWebSocket {
    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols);
      this.addEventListener("message", event => {
        record.frames.push({
          t: Date.now(),
          direction: "received",
          payload: String(event.data)
        });
      });
    }
    override send(data: Parameters<WebSocket["send"]>[0]) {
      record.frames.push({
        t: Date.now(),
        direction: "sent",
        payload: String(data)
      });
      super.send(data);
    }
  }
  globalThis.WebSocket = RecordingWebSocket;
  const commands = {
    record,
    login: (token: string, user: string) => {
      void Promise.all([authStorage.set("token", token), authStorage.set("user", user), authStorage.set("isAuthenticated", "true"), ...PREVIOUS_MEMBER_AUTH_KEYS.map(key => authStorage.remove(key))]).then(() => {
        record.loggedIn = true;
      }, (error: unknown) => {
        record.loginError = String(error);
      });
    },
    open: (url: string) => {
      void Linking.openURL(url);
    },
    track: (members: string[], replies: string[], cards: (string | null)[], others: string[] = []) => {
      targets = {
        members,
        replies,
        cards,
        others
      };
      setUiSampling?.(true);
    },
    hasRow: (text: string) => newestMountedRowFor(text) !== undefined,
    rowBox: (text: string) => box(newestMountedRowFor(text)?.node),
    flip: (n: number) => setBeacon?.(n)
  };
  Reflect.set(globalThis, GLOBAL_KEY, commands);
  const Beacon = () => {
    const [n, setN] = useState(0);
    const uiSampler = useFrameCallback(frame => {
      "worklet";

      // constraint: keyboard events flush frame callbacks again within a frame, with an older timestamp.
      const last = uiLastFrameAt.value;
      if (last !== null && frame.timestamp <= last) {
        return;
      }
      uiLastFrameAt.value = frame.timestamp;
      const started = performance.now();
      const rects = uiSlotRefs.value.map(ref => {
        // constraint: measure() only rejects a -1 tag; a ref with no view yet reads null and throws natively.
        const measured = ref === null || ref() === null ? null : measure(ref);
        return measured === null ? null : [measured.pageY, measured.height] as const;
      });
      const costMs = performance.now() - started;
      uiBuffer.modify(frames => {
        "worklet";

        frames.push({
          t: frame.timestamp,
          wall: Date.now(),
          intervalMs: last === null ? null : frame.timestamp - last,
          costMs,
          rects
        });
        return frames;
      }, false);
    }, false);
    useEffect(() => {
      setUiSampling = uiSampler.setActive;
      // constraint: frames are handed to JS in batches; a per-frame hop would load the JS thread being measured.
      const drain = setInterval(runOnUI(() => {
        "worklet";

        const frames = uiBuffer.value;
        if (frames.length === 0) {
          return;
        }
        uiBuffer.value = [];
        runOnJS(receiveUiFrames)(frames);
      }), UI_FRAME_BATCH_MS);
      return () => {
        clearInterval(drain);
        setUiSampling = null;
      };
    }, [uiSampler]);
    useEffect(() => {
      setBeacon = setN;
      return () => {
        setBeacon = null;
      };
    }, []);
    useEffect(() => {
      if (n > 0) {
        requestAnimationFrame(() => requestAnimationFrame(() => record.beacons.push({
          n,
          t: Date.now()
        })));
      }
    }, [n]);
    return <View pointerEvents="none" style={{
      position: "absolute",
      top: 0,
      right: 0,
      width: BEACON_SIZE,
      height: BEACON_SIZE,
      backgroundColor: n % 2 === 1 ? "#fff" : "#000"
    }} data-sentry-element="View" data-sentry-component="Beacon" data-sentry-source-file="chatHarness.tsx" />;
  };
  const mark = (kind: Mark) => {
    record.events.push({
      t: Date.now(),
      kind
    });
  };

  // constraint: Tamagui keeps the first ref it is given, so the ref must be stable and the text read from the row.
  const useTracked = (registry: Set<Row>, text: string, card = false) => {
    const animatedRef = useAnimatedRef<View>();
    const row = useRef<Row>({
      id: nextRowId++,
      node: null,
      text: "",
      animatedRef
    }).current;
    row.text = text;
    return useCallback((node: unknown) => {
      const t = Date.now();
      if (card && node) {
        mark("card-mount");
      }
      if (registry === rows) {
        record.events.push({
          t,
          kind: node ? "row-mount" : "row-unmount",
          row: row.id,
          text: row.text
        });
      } else if (registry === lists) {
        record.events.push({
          t,
          kind: node ? "list-mount" : "list-unmount"
        });
      }
      row.node = isMeasurable(node) ? node : null;
      if (row.node) {
        record.streamingListMounted ||= registry === lists;
        row.animatedRef(row.node);
        registry.add(row);
      } else {
        registry.delete(row);
      }
    }, [registry, row, card]);
  };
  return {
    Beacon,
    markSend: () => mark("send"),
    mark,
    onScroll: (y: number) => {
      record.events.push({
        t: Date.now(),
        kind: "scroll",
        y
      });
    },
    useJankMarks: ({
      hasLoadingEvent
    }: JankParams) => {
      const hadLoading = useRef(false);
      useEffect(() => {
        if (hadLoading.current && !hasLoadingEvent) {
          mark("row-swap");
        }
        hadLoading.current = hasLoadingEvent;
      }, [hasLoadingEvent]);
    },
    markAuthReady: () => {
      record.authReady = true;
    },
    useStreamingList: () => useTracked(lists, ""),
    useRow: (text: string | null | undefined, {
      card = false
    } = {}) => useTracked(rows, text ?? "", card),
    useComposer: () => useTracked(composers, "")
  };
}

// constraint: NODE_ENV is inlined in every production bundle, so the probe is dead code there whatever the env.
export const chatHarness = process.env.NODE_ENV !== "production" && (process.env.EXPO_PUBLIC_CHAT_HARNESS === "1" || process.env.NEXT_PUBLIC_CHAT_HARNESS === "1") ? createChatHarness() : null;
const noRowRef = () => undefined;
export const useChatHarnessRow = chatHarness?.useRow ?? noRowRef;
export const useChatHarnessComposer = chatHarness?.useComposer ?? noRowRef;
export const useChatHarnessStreamingList = chatHarness?.useStreamingList ?? noRowRef;
function noJankMarks(_params: JankParams): void {}
export const useChatHarnessJank = chatHarness?.useJankMarks ?? noJankMarks;